import Cookies from 'js-cookie';

/** Bump this when the banner text or cookie categories change. */
const CONSENT_VERSION = '1.0.0';
const CONSENT_SESSION_KEY = 'consent_session_id';

interface ConsentPreferences {
  essential?: boolean;
  analytics: boolean;
  marketing: boolean;
}

/**
 * Cookie domain for consent_session_id — same logic as the consent banner's
 * cookieDomain prop in _app.tsx: '.invideo.io' in production, undefined in dev.
 */
function getCookieDomain(): string | undefined {
  return process.env.NEXT_PUBLIC_ENV === 'production'
    ? '.invideo.io'
    : undefined;
}

/**
 * Get or create a consent_session_id UUID stored in a .invideo.io session cookie.
 * Returns undefined during SSR.
 */
function getOrCreateConsentSessionId(): string | undefined {
  if (typeof window === 'undefined') return undefined; // SSR guard

  let id = Cookies.get(CONSENT_SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    const domain = getCookieDomain();
    Cookies.set(CONSENT_SESSION_KEY, id, {
      sameSite: 'lax' as const,
      path: '/',
      ...(domain && { domain }),
      // No expires — session cookie. Transient by design:
      // only needed until linkConsentToUser() runs after login on ai.invideo.io.
    });
  }
  return id;
}

/**
 * Fire-and-forget POST to /api/cookie-consent on pro-api.
 * Called from _app.tsx's onCtaClicked callback.
 */
export function sendConsentLog(
  action: string,
  preferences: ConsentPreferences,
) {
  if (typeof window === 'undefined') return; // SSR guard

  const apiHost = process.env.NEXT_PUBLIC_PRO_API_HOST;
  if (!apiHost) return;

  const consentSessionId = getOrCreateConsentSessionId();

  // Fire-and-forget — never block UI on this call
  fetch(`${apiHost}/api/cookie-consent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action,
      preferences,
      consent_version: CONSENT_VERSION,
      source: 'iv-web-tools',
      consent_session_id: consentSessionId,
    }),
  }).catch(() => {
    // Silently ignore — the cookie is the source of truth
  });
}
